import { useEffect, useState } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { businessesCollection, getAuthToken, syncApiUrl, type Business } from "@cliqsuite/db";
import { avColor, chipStyle, fmtDateShort, initials, subscriptionStatusMeta } from "../lib/format";
import { useAdminData } from "../state/AdminDataContext";
import { DeleteBusinessModal } from "./DeleteBusinessModal";
import { EditSubscriptionModal } from "./EditSubscriptionModal";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "trial", label: "Trial" },
  { key: "expired", label: "Past due" },
  { key: "suspended", label: "Suspended" },
  { key: "inactive", label: "Inactive" },
];

/** Every business with its plan and subscription state; edit moves plan, status and dates. */
export function BusinessesPanel() {
  const { data: businesses, isLoading } = useLiveQuery((q) => q.from({ businesses: businessesCollection }));
  const { plans, businessReassign, businessSetSubscription, wipeBusiness } = useAdminData();
  const [deleting, setDeleting] = useState<Business | null>(null);
  const [editingSub, setEditingSub] = useState<Business | null>(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  // Stores, active staff and the owner's name per business, from the server (Admin doesn't sync tenants' stores, staff or logins).
  const [usage, setUsage] = useState<Map<string, { stores: number; seats: number; owner?: string | null }>>(new Map());
  useEffect(() => {
    fetch(syncApiUrl("/core/businesses/usage/counts"), { headers: getAuthToken() ? { authorization: `Bearer ${getAuthToken()}` } : {} })
      .then((res) => (res.ok ? res.json() : { rows: [] }))
      .then((body: { rows: Array<{ business_id: string; stores: number; seats: number; owner?: string | null }> }) => setUsage(new Map(body.rows.map((r) => [r.business_id, r]))))
      .catch(() => {});
  }, [businesses?.length]);
  const of = (used: number | undefined, limit: number | undefined) => (used === undefined ? "—" : limit === undefined || limit >= 999 ? String(used) : `${used} of ${limit}`);

  const all = [...(businesses ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const count = (k: string) => (k === "all" ? all.length : all.filter((b) => b.subscription_status === k).length);
  const needle = q.trim().toLowerCase();
  const rows = all.filter((b) => (filter === "all" || b.subscription_status === filter) && (!needle || b.name.toLowerCase().includes(needle)));
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <div className="o-toolbar">
        <label className="o-search">
          <span className="ms" aria-hidden="true">search</span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Business name" aria-label="Search businesses" />
        </label>
        <div className="o-chips o-chips--scroll" role="radiogroup" aria-label="Status">
          {FILTERS.filter((f) => f.key === "all" || count(f.key) > 0 || f.key === filter).map((f) => (
            <button type="button" role="radio" aria-checked={filter === f.key} key={f.key} onClick={() => setFilter(f.key)}>
              {f.label} <span className="num" style={{ opacity: 0.55 }}>{count(f.key)}</span>
            </button>
          ))}
        </div>
      </div>

      <section className="o-card">
        {isLoading && <div className="o-muted-note">Loading businesses…</div>}
        {!isLoading && rows.length === 0 && (
          <div className="o-empty">
            <span className="ms" aria-hidden="true">storefront</span>
            <strong>{all.length ? "Nothing matches" : "No businesses yet"}</strong>
          </div>
        )}
        {rows.length > 0 && (
          <div className="o-table" style={{ ["--cols" as string]: "1.6fr 1.1fr 0.9fr 1.2fr 0.7fr 0.8fr 84px" }}>
            <div className="o-table__head">
              <span>Business</span>
              <span>Plan</span>
              <span>Status</span>
              <span>Period ends</span>
              <span>Stores</span>
              <span>Seats</span>
              <span />
            </div>
            {rows.map((biz) => {
              const plan = plans.find((p) => p.id === biz.subscription_plan_id) ?? null;
              const meta = subscriptionStatusMeta(biz.subscription_status);
              const inGrace = !!biz.grace_period_ends_at && biz.grace_period_ends_at >= today;
              return (
                <div key={biz.id} className="o-table__row" style={{ cursor: "default" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span className="o-avatar" style={{ width: 36, height: 36, borderRadius: 11, background: avColor(biz.name), fontSize: 12.5 }}>{initials(biz.name)}</span>
                    {/* Canvas v2-a-businesses: the owner's name under the business. */}
                    <span style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                      <span style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis" }}>{biz.name}</span>
                      {usage.get(biz.id)?.owner && <small style={{ fontSize: 12, fontWeight: 600, color: "var(--text2)", overflow: "hidden", textOverflow: "ellipsis" }}>{usage.get(biz.id)!.owner}</small>}
                    </span>
                  </span>
                  <span>{plan ? plan.name : "No plan"}</span>
                  <span><span className="o-pill" style={chipStyle(meta)}>{meta.label}</span></span>
                  <span>
                    {inGrace ? `Grace until ${fmtDateShort(biz.grace_period_ends_at!)}` : biz.subscription_period_end ? fmtDateShort(biz.subscription_period_end) : "—"}
                  </span>
                  <span className="num">{of(usage.get(biz.id)?.stores, plan?.stores)}</span>
                  <span className="num">{of(usage.get(biz.id)?.seats, plan?.seats)}</span>
                  <span style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    <button type="button" className="o-icon-btn" aria-label={`Edit ${biz.name}`} onClick={() => setEditingSub(biz)}>
                      <span className="ms" aria-hidden="true">edit</span>
                    </button>
                    <button type="button" className="o-icon-btn is-danger" aria-label={`Delete ${biz.name}`} onClick={() => setDeleting(biz)}>
                      <span className="ms" aria-hidden="true">delete</span>
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {deleting && (
        <DeleteBusinessModal business={deleting} onClose={() => setDeleting(null)} onConfirm={(confirmName) => wipeBusiness(deleting.id, confirmName)} />
      )}
      {editingSub && (
        <EditSubscriptionModal
          business={editingSub}
          plans={plans.filter((p) => !p.archived || p.id === editingSub.subscription_plan_id)}
          onClose={() => setEditingSub(null)}
          onConfirm={(form, planKey) => {
            const current = plans.find((p) => p.id === editingSub.subscription_plan_id);
            if (planKey && planKey !== current?.key) businessReassign(editingSub.id, planKey);
            businessSetSubscription(editingSub.id, form);
          }}
        />
      )}
    </div>
  );
}
